import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlatformIChingService } from '@data/_generated-api/platform-api/services/platform-i-ching.service';
import { PlatformDataSourceQuery } from '@data/_generated-api/platform-api/models/platform-data-source-query';
import { PlatformIChingConsultationDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-dto';
import { PlatformIChingConsultationMarkerDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-marker-dto';
import { IChingCastMode, IChingLineValue } from '../core/iching.models';

/** Raadplegingen per pagina in het dagboek. */
export const ICHING_JOURNAL_PAGE_SIZE = 9;

/** Eén raadpleging zoals de pagina ze aanlevert; lijnen en muntzijden als arrays. */
export interface IChingJournalInput {
  /** Zes lijnwaarden, onder naar boven. */
  readonly lines: readonly IChingLineValue[];
  /** Optioneel: zes worpen van drie muntzijden (H/T), bv. "HHT". */
  readonly coinFaces?: readonly string[];
  readonly question: string;
  readonly questionKind: 'question' | 'situation';
  readonly castMode: IChingCastMode;
  readonly keywords: readonly string[];
  readonly notes: string;
  readonly doList: readonly string[];
  readonly dontList: readonly string[];
  /** ISO-tijdstip waarop de raadpleging herbekeken werd. */
  readonly revisitedOn?: string;
  /** Chart waarvan de raadpleging een moment is (levenstijdlijn). */
  readonly linkedChartId?: string;
  /** ISO-tijdstip waarop de raadpleging gaat; leeg = het bewaarmoment (server). */
  readonly consultedAt?: string;
}

/** Reflectievelden die na het bewaren nog wijzigen; de worp zelf ligt vast. */
export type IChingJournalUpdate = Omit<IChingJournalInput, 'lines' | 'coinFaces' | 'castMode' | 'questionKind'>;

/** Eén pagina van het dagboek. */
export interface IChingJournalPage {
  readonly items: PlatformIChingConsultationDto[];
  readonly totalRecordCount: number;
  /** Pagina die de backend leverde: een te hoge pagina valt terug op de laatste. */
  readonly page: number;
}

/** Toestand van het dagboekoverzicht: pagina (1-based) en optioneel hexagramfilter (1..64). */
export interface IChingJournalQuery {
  readonly page: number;
  readonly hexagram?: number;
}

/**
 * Enige laag die de I Tjing-dagboek-API kent (patroon SupportApiService).
 * Alle resultaten volgen het ApiResult-envelope-contract: isValid + value.
 */
@Injectable({ providedIn: 'root' })
export class HdIChingJournalService {
  private readonly api = inject(PlatformIChingService);

  async create(input: IChingJournalInput): Promise<PlatformIChingConsultationDto> {
    const result = await firstValueFrom(
      this.api.postIchingConsultationsActionCreate({
        body: {
          question: input.question,
          questionKind: input.questionKind,
          castMode: input.castMode,
          lines: input.lines.join(','),
          coinFaces: input.coinFaces ? input.coinFaces.join(',') : undefined,
          keywords: [...input.keywords],
          notes: input.notes,
          doList: [...input.doList],
          dontList: [...input.dontList],
          linkedChartId: input.linkedChartId,
          consultedAt: input.consultedAt,
        },
      }),
    );
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De raadpleging kon niet bewaard worden.');
    return result.value;
  }

  async update(id: string, input: IChingJournalUpdate): Promise<PlatformIChingConsultationDto> {
    const result = await firstValueFrom(
      this.api.postIchingConsultationsActionUpdate({
        body: {
          id,
          question: input.question,
          keywords: [...input.keywords],
          notes: input.notes,
          doList: [...input.doList],
          dontList: [...input.dontList],
          revisitedOn: input.revisitedOn,
          linkedChartId: input.linkedChartId,
          consultedAt: input.consultedAt,
        },
      }),
    );
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De raadpleging kon niet bijgewerkt worden.');
    return result.value;
  }

  /**
   * Markers van de eigen raadplegingen bij een chart (oplopend op tijdstip); met `includeUnlinked`
   * ook de raadplegingen zonder chart (de eigen chart).
   */
  async markersForChart(chartId: string, includeUnlinked: boolean): Promise<PlatformIChingConsultationMarkerDto[]> {
    const result = await firstValueFrom(this.api.getIchingConsultationsMineForChartChartid({ chartId, includeUnlinked }));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De I Tjing-momenten konden niet geladen worden.');
    return result.value ?? [];
  }

  /** Verwijdert een eigen raadpleging; een al verdwenen raadpleging (value false) is geen fout. */
  async remove(id: string): Promise<void> {
    const result = await firstValueFrom(this.api.postIchingConsultationsActionDelete({ body: { id } }));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De raadpleging kon niet verwijderd worden.');
  }

  async get(id: string): Promise<PlatformIChingConsultationDto> {
    const result = await firstValueFrom(this.api.getIchingConsultationsMineId({ id }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'De raadpleging kon niet geladen worden.');
    return result.value;
  }

  /** Eén pagina eigen raadplegingen, nieuwste eerst; het hexagram matcht primair en veranderd. */
  async queryMine(query: IChingJournalQuery): Promise<IChingJournalPage> {
    const result = await firstValueFrom(
      this.api.postIchingConsultationsMineQueryDatasource({ hexagram: query.hexagram, body: buildJournalDataSourceQuery(query.page) }),
    );
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'Het dagboek kon niet geladen worden.');
    return {
      items: result.value?.items ?? [],
      totalRecordCount: result.value?.totalRecordCount ?? 0,
      page: result.value?.dataSourceQuery?.pageOption?.currentPageIndex ?? query.page,
    };
  }
}

/**
 * DataSourceQuery voor het dagboek: enkel paging (1-based). Bewust zonder SortOptions zodat de
 * backend de nieuwste raadpleging eerst zet; het hexagram gaat als aparte parameter mee.
 */
function buildJournalDataSourceQuery(page: number): PlatformDataSourceQuery {
  return {
    pageOption: { currentPageIndex: Math.max(1, Math.floor(page)), pageSize: ICHING_JOURNAL_PAGE_SIZE },
    sortOptions: [],
  };
}
